import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { 
  Cat, Heart, Calendar, Phone, Home, Menu, X, User, LogOut, 
  ChevronRight, Star, Shield, Users, Gift, Cookie, CheckCircle, Sparkles, Search, Share2, Camera, Flame
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";
import { Separator } from "./ui/separator";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("Logged out successfully");
    navigate("/");
  };

  // Primary navigation - main services (shown in header bar)
  const primaryNavLinks = [
    { to: "/", label: "Home", icon: Home },
    { to: "/scan-angels", label: "Scan Angels", icon: CheckCircle },
    { to: "/lost-found", label: "Lost & Found", icon: Search },
    { to: "/cats", label: "Adopt", icon: Heart },
    { to: "/gallery", label: "Our Cats", icon: Cat },
    { to: "/book", label: "Cat Sitting", icon: Calendar },
    { to: "/social", label: "Social Media", icon: Share2 },
    { to: "/blog", label: "Blog", icon: Star },
  ];

  // More dropdown - additional pages
  const moreNavLinks = [
    { to: "/community-gallery", label: "Community Gallery", icon: Camera },
    { to: "/in-memoriam", label: "In Memoriam", icon: Flame },
    { to: "/brands", label: "Brands We Love", icon: Sparkles },
    { to: "/shop", label: "Shop", icon: Gift },
    { to: "/donate", label: "Donate", icon: Heart },
    { to: "/volunteer", label: "Volunteer", icon: Users },
    { to: "/reviews", label: "Reviews", icon: Star },
    { to: "/contact", label: "Contact", icon: Phone },
    { to: "/about", label: "About Us", icon: Users },
    { to: "/indoor-outdoor", label: "Indoor vs Outdoor", icon: Home },
    { to: "/food-guide", label: "Food Guide", icon: Cookie },
    { to: "/litter-guide", label: "Litter Guide", icon: Star },
    { to: "/microchipping", label: "Microchipping", icon: Shield },
  ];

  const navLinks = [...primaryNavLinks.slice(0, -1), ...moreNavLinks, ...primaryNavLinks.slice(-1)];

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white/90 backdrop-blur-sm shadow-sm"}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 md:py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_feline-friends-11/artifacts/06g3ts97_IMG_0597.jpeg" 
              alt="CatPals+ Logo" 
              className="w-[80px] h-[80px] md:w-[100px] md:h-[100px] lg:w-[120px] lg:h-[120px] rounded-full object-cover group-hover:scale-110 transition-transform"
            />
            <span className="text-2xl font-bold text-stone-900 font-serif hidden sm:inline">CatPals<span className="text-amber-600">+</span></span>
          </Link>

          <nav className="hidden lg:flex items-center justify-center flex-1 mx-2 xl:mx-4">
            <div className="flex items-center gap-1 xl:gap-3">
              {primaryNavLinks.map(({ to, label }) => (
                <Link 
                  key={to} 
                  to={to} 
                  className="text-stone-700 hover:text-amber-600 font-medium transition-colors text-xs xl:text-sm whitespace-nowrap px-2 py-1 rounded-md hover:bg-amber-50" 
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {label}
                </Link>
              ))}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="text-stone-700 hover:text-amber-600 font-medium transition-colors text-xs xl:text-sm whitespace-nowrap px-2 py-1 rounded-md hover:bg-amber-50 flex items-center gap-1" data-testid="nav-more-trigger">
                    More <ChevronRight className="w-3 h-3 rotate-90" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-48">
                  {moreNavLinks.map(({ to, label, icon: Icon }) => (
                    <DropdownMenuItem key={to} onClick={() => navigate(to)} data-testid={`nav-more-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <Icon className="w-4 h-4 mr-2" /> {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </nav>

          <div className="hidden lg:flex items-center gap-2 xl:gap-4 flex-shrink-0">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="user-menu-btn">
                    <Avatar className="w-8 h-8">
                      <AvatarImage src={user.picture} />
                      <AvatarFallback className="bg-amber-100 text-amber-700">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="font-medium hidden xl:inline">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="dashboard-link">
                    <Calendar className="w-4 h-4 mr-2" /> My Bookings
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="admin-link">
                      <Shield className="w-4 h-4 mr-2" /> Admin Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                    <LogOut className="w-4 h-4 mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={() => navigate("/login")} className="bg-amber-600 hover:bg-amber-700 rounded-full px-4 xl:px-6 text-sm" data-testid="login-btn">
                Sign In
              </Button>
            )}
          </div>

          <button className="lg:hidden p-2" onClick={() => setIsOpen(!isOpen)} data-testid="mobile-menu-toggle">
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {isOpen && (
          <nav className="lg:hidden pt-4 pb-2 animate-fadeIn">
            <div className="max-h-[60vh] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-amber-300 scrollbar-track-transparent">
              {navLinks.map(({ to, label, icon: Icon }) => (
                <Link key={to} to={to} onClick={() => setIsOpen(false)} className="flex items-center gap-3 py-3 text-stone-700 hover:text-amber-600 hover:bg-amber-50 px-2 rounded-lg transition-colors">
                  <Icon className="w-5 h-5 flex-shrink-0" /> <span className="truncate">{label}</span>
                </Link>
              ))}
            </div>
            <Separator className="my-2" />
            {user ? (
              <div className="space-y-1">
                <Link to="/dashboard" onClick={() => setIsOpen(false)} className="flex items-center gap-3 py-3 text-stone-700 hover:bg-amber-50 px-2 rounded-lg transition-colors">
                  <Calendar className="w-5 h-5 flex-shrink-0" /> My Bookings
                </Link>
                {user.is_admin && (
                  <Link to="/admin" onClick={() => setIsOpen(false)} className="flex items-center gap-3 py-3 text-amber-600 font-medium hover:bg-amber-50 px-2 rounded-lg transition-colors">
                    <Shield className="w-5 h-5 flex-shrink-0" /> Admin Panel
                  </Link>
                )}
                <button onClick={() => { handleLogout(); setIsOpen(false); }} className="flex items-center gap-3 py-3 text-stone-700 w-full hover:bg-red-50 px-2 rounded-lg transition-colors">
                  <LogOut className="w-5 h-5 flex-shrink-0" /> Logout
                </button>
              </div>
            ) : (
              <Link to="/login" onClick={() => setIsOpen(false)} className="flex items-center gap-3 py-3 text-amber-600 font-medium hover:bg-amber-50 px-2 rounded-lg transition-colors">
                <User className="w-5 h-5 flex-shrink-0" /> Sign In
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
    </>
  );
};

export default Header;
